import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  AssistantPanelContext,
  type AssistantPanelContextValue,
  type AssistantPanelView,
} from './assistantPanelContext';
import type { AssistantBriefKind } from '@/api/assistantProactive/assistantProactiveApi.types';
import {
  ASSISTANT_AUTO_NAVIGATE_STORAGE_KEY_PREFIX,
  ASSISTANT_PANEL_SHOWS_PREFERENCE_TOGGLES,
} from '@/constants/assistant';
import { useAuth } from '@/hooks/useAuth';

function readAutoNavigatePreference(storageKey: string): boolean {
  if (!ASSISTANT_PANEL_SHOWS_PREFERENCE_TOGGLES) {
    return true;
  }
  try {
    return window.localStorage.getItem(storageKey) !== 'false';
  } catch (storageError) {
    void storageError;
    return true;
  }
}

function writeAutoNavigatePreference(storageKey: string, isEnabled: boolean): void {
  try {
    window.localStorage.setItem(storageKey, isEnabled ? 'true' : 'false');
  } catch (storageError) {
    void storageError;
  }
}

export function AssistantPanelProvider({ children }: { children: ReactNode }) {
  const { currentUser, activeTenantId } = useAuth();
  const storageKey =
    ASSISTANT_AUTO_NAVIGATE_STORAGE_KEY_PREFIX + (currentUser === null ? '' : currentUser.id);
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<AssistantPanelView>('chat');
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [aboutContext, setAboutContext] = useState<string | null>(null);
  const [pendingQuestion, setPendingQuestion] = useState<string | null>(null);
  const [pendingBriefKind, setPendingBriefKind] = useState<AssistantBriefKind | null>(null);
  const [isAutoNavigateEnabled, setIsAutoNavigateEnabled] = useState(function readInitial() {
    return readAutoNavigatePreference(storageKey);
  });

  useEffect(
    function forgetConversationOfPreviousWorkspace() {
      setConversationId(null);
      setAboutContext(null);
      setPendingQuestion(null);
      setPendingBriefKind(null);
      setView('chat');
    },
    [activeTenantId]
  );

  const open = useCallback(function openPanel() {
    setIsOpen(true);
  }, []);
  const close = useCallback(function closePanel() {
    setIsOpen(false);
  }, []);
  const toggle = useCallback(function togglePanel() {
    setIsOpen(function flip(current) {
      return !current;
    });
  }, []);
  const selectConversation = useCallback(function select(nextConversationId: string | null) {
    setConversationId(nextConversationId);
    setAboutContext(null);
    setView('chat');
  }, []);
  const askAbout = useCallback(function ask(nextAboutContext: string, question: string) {
    setConversationId(null);
    setAboutContext(nextAboutContext);
    setPendingQuestion(question);
    setView('chat');
    setIsOpen(true);
  }, []);
  const consumePendingQuestion = useCallback(
    function consume() {
      const question = pendingQuestion;
      setPendingQuestion(null);
      return question;
    },
    [pendingQuestion]
  );
  const requestBrief = useCallback(function requestDailyBrief(kind: AssistantBriefKind) {
    setConversationId(null);
    setAboutContext(null);
    setPendingBriefKind(kind);
    setView('chat');
    setIsOpen(true);
  }, []);
  const consumePendingBrief = useCallback(
    function consumeBrief() {
      const kind = pendingBriefKind;
      setPendingBriefKind(null);
      return kind;
    },
    [pendingBriefKind]
  );
  const setAutoNavigateEnabled = useCallback(
    function updatePreference(isEnabled: boolean) {
      setIsAutoNavigateEnabled(isEnabled);
      writeAutoNavigatePreference(storageKey, isEnabled);
    },
    [storageKey]
  );

  const value = useMemo(
    function buildValue(): AssistantPanelContextValue {
      return {
        isOpen,
        open,
        close,
        toggle,
        view,
        setView,
        conversationId,
        selectConversation,
        aboutContext,
        askAbout,
        pendingQuestion,
        consumePendingQuestion,
        requestBrief,
        pendingBriefKind,
        consumePendingBrief,
        isAutoNavigateEnabled,
        setAutoNavigateEnabled,
      };
    },
    [
      isOpen,
      open,
      close,
      toggle,
      view,
      conversationId,
      selectConversation,
      aboutContext,
      askAbout,
      pendingQuestion,
      consumePendingQuestion,
      requestBrief,
      pendingBriefKind,
      consumePendingBrief,
      isAutoNavigateEnabled,
      setAutoNavigateEnabled,
    ]
  );

  return <AssistantPanelContext.Provider value={value}>{children}</AssistantPanelContext.Provider>;
}
